import { io } from "socket.io-client";
import { useSocketStore } from "../store/socketStore";
import { useRuleBasedInboxStore } from "../store/ruleBasedInboxStore";
import agent from "../services/api";
import { ACTION_TYPE_NAME } from "../components/dashboard/flowBuilder/flowBuilderConstants";
import { getVendorField } from "./whiteLabelling";
import { actionTypesToIgnoreForThreadMetaDataUpdate, actionTypesToIgnoreWhenLastMessageUpdation, VOICE_CALL_STATUS } from "../pages/ruleBasedInbox/constants";
import { useVoiceCallStore } from "../store/voiceCallStore";
import { useGenericStore } from "../store/genericStore";
import {
    getVoiceCallAcceptedMessage,
    getVoiceCallEndedMessage,
    mapBusinessPhoneToWabaDetails,
} from "./voiceCallDetails";
import { formatDateAccordingToTimezoneUsingMoment } from "./constants";
import { checkIfUserIsAtBottomInChatComponent, getThreadVisibilityDecision, isAgentInfoActionType, mapThreadExecutionToChatMessages, scrollToBottomInChat, sortThreadMessageByTimeStamp } from "../pages/ruleBasedInbox/utils";

// Maximum reconnection attempts before giving up
const MAX_RECONNECT_ATTEMPTS = 4;
// Minimum connection duration (in ms) to consider a connection stable
// If connection disconnects before this time, it's considered an immediate disconnect
const MIN_STABLE_CONNECTION_TIME = 10000; // 5 seconds

// ============ Rule-Based Inbox Helpers ============

/**
 * Build last_message object from socket data for thread list
 */
const buildLastMessageFromSocketData = (threadExecutionData, socketData) => {
    if (!threadExecutionData || threadExecutionData.length === 0) return null;

    // Get the last execution data entry
    const lastExecution = threadExecutionData[threadExecutionData.length - 1];

    return {
        thread_last_message_id: lastExecution.thread_execution_log_id,
        thread_id: socketData.thread_id,
        timestamp: lastExecution.step_end_timestamp
            ? new Date(lastExecution.step_end_timestamp).getTime()
            : new Date(lastExecution.step_start_timestamp).getTime(),
        action_type_name: lastExecution.action_type_name,
        action_type_id: lastExecution.action_type_id,
        thread_last_message_data: {
            action_type_name: lastExecution.action_type_name,
            user_assignment_id: lastExecution.user_assignment_id,
            action_execution_data: lastExecution.action_execution_data || {},
            chatbot_data: lastExecution.chatbot_data,
            user_first_name: lastExecution.user_first_name,
            user_last_name: lastExecution.user_last_name,
        },
        thread_execution_log_id: lastExecution.thread_execution_log_id,
        user_assignment_id: lastExecution.user_assignment_id,
        user_first_name: lastExecution.user_first_name,
        user_last_name: lastExecution.user_last_name,
        user_email: lastExecution.user_email,
    };
};

const addNewRuleBasedInboxThread = (data) => {
    // Read fresh state to avoid stale state issues in production builds
    const state = useRuleBasedInboxStore.getState();
    const { addNewThreadToList, stats, filters, userAssignmentId } = state;

    // Get action type from thread_execution_data
    const actionTypeName = data?.thread_execution_data?.[0]?.action_type_name || "";

    // Check if thread already exists in the list - read fresh state
    const currentThreads = useRuleBasedInboxStore.getState().threads;
    const threadExistsInList = currentThreads.some((t) => t.thread_id === data?.thread_id);

    // Build the thread object for filter checking
    const threadForCheck = {
        flag_archive: data?.flag_archive || false,
        manual_agent_data: data?.manual_agent_data || [],
        thread_last_activity_inbox_criteria: data?.thread_last_activity_inbox_criteria,
        thread_status: Array.isArray(data?.thread_status) ? null : data?.thread_status,
        thread_ticket_data: data?.thread_ticket_data ? data?.thread_ticket_data : [],
        thread_tags: data?.thread_tags || [],
        channel_id: data?.channel_id,
        thread_metadata: data?.thread_metadata || [],
        lastMessage: buildLastMessageFromSocketData(data?.thread_execution_data, data),
    };

    // Get current page start_from
    const startFrom = typeof stats.start_from === "string"
        ? parseInt(stats.start_from, 10)
        : stats.start_from;

    // Get visibility decision
    const decision = getThreadVisibilityDecision(threadForCheck, {
        filters,
        userAssignmentId,
        threadExistsInList,
        startFrom,
        actionTypeName,
    });

    // Handle the decision
    switch (decision.action) {
        case "add": {
            // Build last_message from thread_execution_data
            const lastMessage = buildLastMessageFromSocketData(data?.thread_execution_data, data);

            // Don't set last_message for manual agent action types
            const shouldExcludeLastMessage = actionTypeName && actionTypesToIgnoreWhenLastMessageUpdation.includes(actionTypeName);

            const newThread = {
                channel_id: data?.channel_id,
                channel_name: data?.channel_name,
                integrated_channel_id: data?.integrated_channel_id,
                thread_id: data?.thread_id,
                channel_content_type_id: data?.channel_content_type_id,
                channel_content_type_name: data?.channel_content_type_name,
                flag_manual: data?.flag_manual || false,
                flag_snooze: data?.flag_snooze || false,
                thread_start_timestamp: data?.thread_start_timestamp,
                thread_last_updated_timestamp: data?.thread_last_updated_timestamp,
                thread_last_activity_inbox_criteria: data?.thread_last_activity_inbox_criteria,
                thread_metadata: data?.thread_metadata || [],
                manual_agent_data: data?.manual_agent_data || [],
                thread_status: Array.isArray(data?.thread_status) ? null : data?.thread_status,
                thread_tags: data?.thread_tags || [],
                ...(shouldExcludeLastMessage ? {} : { last_message: lastMessage }),
                flag_highlight_thread: true,
                flag_new_messages: true,
            };

            addNewThreadToList(newThread);

            // Remove highlight after 1.5 seconds
            setTimeout(() => {
                const { removeThreadHighlight } = useRuleBasedInboxStore.getState();
                removeThreadHighlight(data?.thread_id);
            }, 1500);
            break;
        }

        case "update": {
            // Thread exists and matches filters - update it
            // Read fresh state to get current selectedThreadId
            const currentState = useRuleBasedInboxStore.getState();
            const { selectedThreadId, markThreadAsRecent, isThreadRecent, hasThreadLastActivityInboxCriteriaChanged } = currentState;

            const lastActivityInboxCriteriaTimeStamp = data?.thread_last_activity_inbox_criteria;
            const lastMessageTime = data?.thread_last_updated_timestamp;
            const lastMessage = buildLastMessageFromSocketData(data?.thread_execution_data, data);

            // Check If thread is not currently selected
            const isThreadNotSelected = selectedThreadId !== data?.thread_id;

            // Check If Message Status Change (used only for highlight — not for list reorder)
            const messageStatusData = data?.thread_execution_data?.[0]?.action_execution_data?.whatsapp_message_status;
            const isMessageStatusChange = messageStatusData && messageStatusData?.length > 0;

            // Check If thread_last_activity_inbox_criteria change
            const isLastActivityInboxCriteriaChanged = hasThreadLastActivityInboxCriteriaChanged(data?.thread_id, lastActivityInboxCriteriaTimeStamp);

            // Check If thread needs to highlight
            const doHighlight = isThreadNotSelected && !isMessageStatusChange && isLastActivityInboxCriteriaChanged;

            // Only newly-becoming-Recent threads move to the bottom of the Recent group.
            // Threads that already have the Recent label keep their current position.
            // Skip pure message-status updates (delivered/read) — those should not reorder.
            const wasAlreadyRecent = isThreadRecent(data?.thread_id);
            const shouldPlaceAtBottomOfRecent =
                startFrom === 1 && !isMessageStatusChange && isLastActivityInboxCriteriaChanged && !wasAlreadyRecent;
            if (shouldPlaceAtBottomOfRecent) {
                markThreadAsRecent(data?.thread_id);
            }

            // Only pass manual_agent_data if it's provided and has items
            const agentData = data?.manual_agent_data && data?.manual_agent_data.length > 0 ? data?.manual_agent_data : [];

            // Get fresh reference to updateThreadInList to ensure we're using the latest function
            const { updateThreadInList: updateThreadFn } = useRuleBasedInboxStore.getState();
            updateThreadFn(
                data?.thread_id,
                lastMessageTime,
                lastActivityInboxCriteriaTimeStamp,
                doHighlight,
                doHighlight,
                lastMessage,
                agentData,
                shouldPlaceAtBottomOfRecent
            );

            // Remove highlight after 1.5 seconds
            if (doHighlight) {
                setTimeout(() => {
                    const { removeThreadHighlight: removeHighlight } = useRuleBasedInboxStore.getState();
                    removeHighlight(data?.thread_id);
                }, 1500);
            }
            break;
        }

        case "remove": {
            // Thread exists but no longer matches filters - remove it
            const { threads: currentThreads } = useRuleBasedInboxStore.getState();
            useRuleBasedInboxStore.setState({
                threads: currentThreads.filter((t) => t.thread_id !== data?.thread_id)
            });
            break;
        }

        case "ignore":
        default:
            break;
    }
};

const handleRuleBasedInboxActivity = (data) => {
    const { updateThreadMetadata, selectedThreadId, threadCache, currentActiveChatbotThread, setCurrentActiveChatbotThread } = useRuleBasedInboxStore.getState();

    const thread_id = data?.thread_id;

    updateThreadMetadata(thread_id, {
        thread_status: data?.thread_status,
        thread_tags: data?.thread_tags,
        thread_notes: data?.thread_notes,
        thread_ticket_data: data?.thread_ticket_data,
        flag_archive: data?.flag_archive,
    });

    const cachedEntry = threadCache[thread_id];
    const isOpenThread = selectedThreadId === thread_id && currentActiveChatbotThread;

    if (isOpenThread) {
        const updatedCurrentThread = {
            ...currentActiveChatbotThread,
            thread_status: data?.thread_status ?? currentActiveChatbotThread?.thread_status,
            thread_tags: data?.thread_tags ?? currentActiveChatbotThread?.thread_tags,
            thread_notes: data?.thread_notes ?? currentActiveChatbotThread?.thread_notes,
            thread_ticket_data: data?.thread_ticket_data ?? currentActiveChatbotThread?.thread_ticket_data,
            flag_archive: data?.flag_archive ?? currentActiveChatbotThread?.flag_archive
        };
        setCurrentActiveChatbotThread(updatedCurrentThread);

        if (cachedEntry) {
            useRuleBasedInboxStore.setState((state) => ({
                threadCache: {
                    ...state.threadCache,
                    [thread_id]: {
                        ...cachedEntry,
                        data: updatedCurrentThread,
                    },
                },
            }));
        }
    } else if (cachedEntry?.data) {
        // Update only the cached thread's own data — never spread the currently
        // open thread into another thread's cache entry.
        const updatedCachedData = {
            ...cachedEntry.data,
            thread_status: data?.thread_status ?? cachedEntry.data?.thread_status,
            thread_tags: data?.thread_tags ?? cachedEntry.data?.thread_tags,
            thread_notes: data?.thread_notes ?? cachedEntry.data?.thread_notes,
            thread_ticket_data: data?.thread_ticket_data ?? cachedEntry.data?.thread_ticket_data,
            flag_archive: data?.flag_archive ?? cachedEntry.data?.flag_archive,
        };
        useRuleBasedInboxStore.setState((state) => ({
            threadCache: {
                ...state.threadCache,
                [thread_id]: {
                    ...cachedEntry,
                    data: updatedCachedData,
                },
            },
        }));
    }
};

// Handle manual agent actions for ruleBasedInbox
const handleRuleBasedInboxManualAgents = (data, newMessages, isChatOpen) => {
    const { threadCache, currentActiveChatbotThread, setCurrentActiveChatbotThread } = useRuleBasedInboxStore.getState();
    // Check if any message is a manual agent action
    const agentActionMessages = newMessages?.filter((message) => {
        const action_type_name = message?.action_type_name ?? "";
        return isAgentInfoActionType(action_type_name);
    });
    
    if (agentActionMessages && agentActionMessages.length > 0) {
        // Update thread list with new manual_agent_data
        // updateThreadMetadata(data.thread_id, {
        //     manual_agent_data: data?.manual_agent_data ?? [],
        // });
            
        const cachedEntry = threadCache[data?.thread_id];
        const isOpenThread = isChatOpen && currentActiveChatbotThread &&
            currentActiveChatbotThread.thread_id === data.thread_id;

        if (isOpenThread) {
            const updatedCurrentThread = {
                ...currentActiveChatbotThread,
                manual_agent_data: data?.manual_agent_data ?? [],
            };
            setCurrentActiveChatbotThread(updatedCurrentThread);

            if (cachedEntry) {
                useRuleBasedInboxStore.setState((state) => ({
                    threadCache: {
                        ...state.threadCache,
                        [data?.thread_id]: {
                            ...cachedEntry,
                            data: updatedCurrentThread,
                        },
                    },
                }));
            }
        } else if (cachedEntry?.data) {
            const updatedCachedData = {
                ...cachedEntry.data,
                manual_agent_data: data?.manual_agent_data ?? [],
            };
            useRuleBasedInboxStore.setState((state) => ({
                threadCache: {
                    ...state.threadCache,
                    [data?.thread_id]: {
                        ...cachedEntry,
                        data: updatedCachedData,
                    },
                },
            }));
        }
    }
}
const updateOrInsertNewMessage = (chatbot_thread_data, currentChat) => {
    const updatedChatbotThreadData = [...(chatbot_thread_data ?? [])];

    currentChat?.forEach((thread) => {
        const threadExecutionId = thread?.thread_execution_log_id;

        // Only match on a real ID. Avoid matching pending optimistic rows (null/undefined id)
        // against each other, which would corrupt thread updates.
        const existingIndex = threadExecutionId
            ? updatedChatbotThreadData.findIndex((msg) => msg?.thread_execution_log_id === threadExecutionId)
            : -1;

        if (existingIndex !== -1) {
            // Update the existing message.
            // Used for read/deliver status updates, and for clearing optimistic pending state
            // once the API has set thread_execution_log_id via reconcilePendingManualMessage.
            updatedChatbotThreadData[existingIndex] = {
                ...updatedChatbotThreadData[existingIndex],
                ...thread,
                isPending: false,
            };
        } else {
            // Append New Message
            updatedChatbotThreadData.push(thread);
        }
    })

    return updatedChatbotThreadData;
}

const getVoiceCallExecutionData = (data) => {
    if (!data) return null;

    if (data.meta_call_id && data.whatsapp_call_status_name) {
        return data;
    }

    const voiceCallThread = data?.thread_execution_data?.find(
        (thread) => thread?.action_type_name?.toLowerCase() === ACTION_TYPE_NAME["VOICE_CALL"]?.toLowerCase()
    );
    return voiceCallThread?.action_execution_data ?? data?.thread_execution_data?.[0]?.action_execution_data;
};

const getVoiceCallDetails = (data) => {
    if (!data) return null;

    if (data.end_user || data.business_phone) {
        return data;
    }

    const voiceCallThread = data?.thread_execution_data?.find(
        (thread) => thread?.action_type_name?.toLowerCase() === ACTION_TYPE_NAME["VOICE_CALL"]?.toLowerCase()
    );
    return voiceCallThread?.voice_call_details ?? data?.voice_call_details ?? null;
};

const resolveWabaDetailsForVoiceCall = (data, voiceCallDetails, getWabaNameAndPhoneNumberBytIntegratedChannelId) => {
    const wabaFromVoiceCallDetails = mapBusinessPhoneToWabaDetails(voiceCallDetails?.business_phone);
    if (wabaFromVoiceCallDetails) return wabaFromVoiceCallDetails;

    return getWabaNameAndPhoneNumberBytIntegratedChannelId(
        data?.channel_name,
        data?.integrated_channel_id,
        data?.meta_integration_whatsapp_id,
    );
};

const dismissInboundCallPopupOnOtherDevices = (metaCallId) => {
    const { isCallConnected, setIsCallConnected, voiceCallId, isCalling, setIsCalling, stopCallTimer, setVoiceCallId, flagVoiceCallInboundPopup, flagInboundCallNotification, inboundCallExecutionData, clearInboundCallState } = useVoiceCallStore.getState();

    const inboundPopupCallId = inboundCallExecutionData?.meta_call_id;
    const isInboundCallActiveForCall = (flagVoiceCallInboundPopup || flagInboundCallNotification) && (
        inboundPopupCallId === metaCallId
        || (!inboundPopupCallId && voiceCallId === metaCallId)
    );
    const answeredOnThisDevice = voiceCallId === metaCallId && (isCalling || isCallConnected);

    if (isInboundCallActiveForCall && !answeredOnThisDevice) {
        clearInboundCallState();
        setVoiceCallId("");
        stopCallTimer();
        if (isCallConnected) setIsCallConnected(false);
        if (isCalling) setIsCalling(false);
    }
};

const handleVoiceCallStatus = (data) => {
    const { isCallConnected, setIsCallConnected, voiceCallId, isCalling, setIsCalling, stopCallTimer, 
        setVoiceCallId, flagVoiceCallInboundPopup, flagInboundCallNotification, setCallingWabaDetails, 
        inboundCallExecutionData, setVoiceCallDetails, showInboundCallNotification, clearInboundCallState 
    } = useVoiceCallStore.getState();

    const { getWabaNameAndPhoneNumberBytIntegratedChannelId } = useRuleBasedInboxStore.getState();

    const actionExecutionData = getVoiceCallExecutionData(data);
    if (!actionExecutionData) return;

    const voiceCallDetails = getVoiceCallDetails(data);
    const isInbound = Boolean(actionExecutionData?.flag_voice_direction_inbound);
    const callStatus = actionExecutionData?.whatsapp_call_status_name?.toLowerCase();
    const metaCallId = actionExecutionData?.meta_call_id;
    const isSameCallSession = voiceCallId === metaCallId;
    const answeredOnThisDevice = isSameCallSession && (isCalling || isCallConnected);
    const inboundPopupCallId = inboundCallExecutionData?.meta_call_id;
    const isInboundCallActive = flagVoiceCallInboundPopup || flagInboundCallNotification;
    const isInboundPopupForCall = isInboundCallActive && (
        inboundPopupCallId === metaCallId
        || voiceCallId === metaCallId
        || (!inboundPopupCallId && !voiceCallId)
    );

    // console.log(isSameCallSession, "isSameCallSession", inboundPopupCallId, "inboundPopupCallId", isInboundPopupForCall, "isInboundPopupForCall", isInbound, "IsInbound", callStatus) 
    const { showSuccess } = useGenericStore.getState();
    if (isInbound) {
        switch (callStatus) {
            case VOICE_CALL_STATUS["RINGING"]?.toLowerCase():
                showInboundCallNotification({
                    callData: data,
                    executionData: actionExecutionData,
                    voiceCallDetails,
                    wabaDetails: resolveWabaDetailsForVoiceCall(
                        data,
                        voiceCallDetails,
                        getWabaNameAndPhoneNumberBytIntegratedChannelId,
                    ),
                });
                setVoiceCallId(metaCallId);
                break;
            case VOICE_CALL_STATUS["ACCEPTED"]?.toLowerCase():
                // Inbound "connected" UI is driven by WebRTC ICE in voiceAIInboundPopup, not socket status
                break;
            case VOICE_CALL_STATUS["CONNECTED"]?.toLowerCase():
                if (voiceCallDetails) setVoiceCallDetails(voiceCallDetails);
                dismissInboundCallPopupOnOtherDevices(metaCallId);
                if (!answeredOnThisDevice) {
                    showSuccess(getVoiceCallAcceptedMessage(voiceCallDetails));
                }
                break;
            case VOICE_CALL_STATUS["COMPLETED"]?.toLowerCase():
            case VOICE_CALL_STATUS["REJECTED"]?.toLowerCase():
            case VOICE_CALL_STATUS["FAILED"]?.toLowerCase():
                if (isSameCallSession || isInboundPopupForCall) {
                    clearInboundCallState();
                    setVoiceCallId("");
                    stopCallTimer();
                    if (isCallConnected) setIsCallConnected(false);
                    if (isCalling) setIsCalling(false);
                    showSuccess(getVoiceCallEndedMessage(callStatus, voiceCallDetails));
                }
                // Show the ended toast even when local call state was already cleared at CONNECTED
                // (e.g. inbound calls handled by an AI voice agent or answered on another device).
                break;
            default:
                break;
        }
        return;
    }

    if (isSameCallSession) {
        const { setFlagVoiceCallPopup } = useVoiceCallStore.getState();
        if (voiceCallDetails) {
            setVoiceCallDetails(voiceCallDetails);
            const wabaFromVoiceCallDetails = mapBusinessPhoneToWabaDetails(voiceCallDetails.business_phone);
            if (wabaFromVoiceCallDetails) setCallingWabaDetails(wabaFromVoiceCallDetails);
        }
        switch (callStatus) {
            case VOICE_CALL_STATUS["ACCEPTED"]?.toLowerCase():
                if (!isCallConnected) setIsCallConnected(true);
                if (!isCalling) setIsCalling(true);
                break;
            case VOICE_CALL_STATUS["COMPLETED"]?.toLowerCase():
            case VOICE_CALL_STATUS["REJECTED"]?.toLowerCase():
            case VOICE_CALL_STATUS["FAILED"]?.toLowerCase():
                showSuccess(getVoiceCallEndedMessage(callStatus, voiceCallDetails));
                if (isCallConnected) setIsCallConnected(false);
                if (isCalling) setIsCalling(false);
                setFlagVoiceCallPopup(false);
                setVoiceCallDetails(null);
                stopCallTimer();
                break;
            default:
                break;
        }
    }
};

export const AddMessage = (data) => {
    const { currentActiveChatbotThread, setCurrentActiveChatbotThread, flagNeedToShowDetailedLogBtn,
        flagViewDetailedLog, sourceDetails, setSourceDetails, userTimeZoneHour 
    } = useRuleBasedInboxStore.getState();

    const notification_type = data?.notification_type;
    console.log(`Add Message is: `, data);

    const currentPath = window.location.pathname?.toLowerCase();
    const isRuleBasedInbox = currentPath == "/inbox" || currentPath == "/rulebasedinbox";

    switch (notification_type) {

        case "static_workflow_inbox":
            const thread_id = data?.data?.thread_id;
            const action_type_name = data?.data?.thread_execution_data?.[0]?.action_type_name;
            const isVoiceCallNotification = data?.data?.thread_execution_data?.some(
                (thread) => thread?.action_type_name?.toLowerCase() === ACTION_TYPE_NAME["VOICE_CALL"]?.toLowerCase()
            ) || Boolean(getVoiceCallExecutionData(data?.data)?.whatsapp_call_status_name);

            if (isVoiceCallNotification) {
                handleVoiceCallStatus(data?.data);
            }
            const searchParams = new URLSearchParams(window.location.search);
            const chatbotId = searchParams.get("chatbot_user_id") ?? currentActiveChatbotThread?.thread_id ?? "";

            if (isRuleBasedInbox) {
                const { threads } = useRuleBasedInboxStore.getState();
                const ruleBasedThread = threads?.find((thread) => String(thread?.thread_id) === String(thread_id));

                // Check if the chat is currently open (using URL param - consistent for both pages)
                const isChatOpen = chatbotId && String(chatbotId) === String(thread_id);

                // Let addNewRuleBasedInboxThread handle add/update/remove decisions
                if (!actionTypesToIgnoreForThreadMetaDataUpdate.includes(action_type_name)) {
                    requestAnimationFrame(() => {
                        addNewRuleBasedInboxThread(data?.data);
                    });
                }
                
                if (isChatOpen) { // If Chat of new message is open
                    const currentChat = mapThreadExecutionToChatMessages(data?.data?.thread_execution_data, {
                        channelName: data.data.channel_name,
                        flagViewDetailedLog,
                        flagNeedToShowDetailedLogBtn: flagNeedToShowDetailedLogBtn,
                    });

                    const updatedThread = {
                        ...currentActiveChatbotThread,
                        //@ts-ignore
                        // Update manual_agent_data if provided in socket data

                        chatbot_thread_data: sortThreadMessageByTimeStamp(updateOrInsertNewMessage(currentActiveChatbotThread?.chatbot_thread_data, currentChat)),
                        thread_execution_data: sortThreadMessageByTimeStamp(updateOrInsertNewMessage(currentActiveChatbotThread?.thread_execution_data, currentChat)),
                        // manual_agent_data: data?.data?.manual_agent_data ?? [],
                    };
                    setCurrentActiveChatbotThread(updatedThread);

                    // Update Last Activity Timestamp in Info Tab.
                    const chatbotThreadData = updatedThread?.chatbot_thread_data;
                    const updatedSourceDetails = {
                        ...sourceDetails,
                        Last_activity_at: formatDateAccordingToTimezoneUsingMoment(chatbotThreadData?.[chatbotThreadData?.length - 1]?.timestamp, userTimeZoneHour, "all") ?? sourceDetails?.Last_activity_at ?? null
                    }
                    setSourceDetails(updatedSourceDetails);

                    // Keep cache in sync with the latest thread state
                    const { threadCache } = useRuleBasedInboxStore.getState();
                    const cachedEntry = threadCache[thread_id];
                    if (cachedEntry) {
                        useRuleBasedInboxStore.setState((state) => ({
                            threadCache: {
                                ...state.threadCache,
                                [thread_id]: {
                                    ...cachedEntry,
                                    data: updatedThread,
                                },
                            },
                        }));
                    }

                    if (checkIfUserIsAtBottomInChatComponent()) { // If User is already at bottom, then scroll to bottom for new message.
                        scrollToBottomInChat();
                    }
                } else { // If Chat of new message is not open.
                    // For rule-based inbox: update the thread cache so reopening is instant
                    const { threadCache } = useRuleBasedInboxStore.getState();
                    const cachedEntry = threadCache[thread_id];
                    if (cachedEntry && cachedEntry.data) {
                        // Format messages the same way as the isChatOpen branch so the
                        // cache stays consistent with what the thread view expects.
                        const formattedMessages = mapThreadExecutionToChatMessages(data?.data?.thread_execution_data, {
                            channelName: data.data.channel_name,
                            flagViewDetailedLog,
                            flagNeedToShowDetailedLogBtn: flagNeedToShowDetailedLogBtn,
                        });

                        const updatedCachedData = {
                            ...cachedEntry.data,
                            chatbot_thread_data: sortThreadMessageByTimeStamp(
                                updateOrInsertNewMessage(cachedEntry.data?.chatbot_thread_data, formattedMessages)
                            ),
                        };

                        useRuleBasedInboxStore.setState((state) => ({
                            threadCache: {
                                ...state.threadCache,
                                [thread_id]: {
                                    ...cachedEntry,
                                    data: updatedCachedData,
                                },
                            },
                        }));
                    }
                }

                if(!ruleBasedThread){
                    // Add new thread to rule-based inbox
                    // Use requestAnimationFrame to ensure state is fresh
                    requestAnimationFrame(() => {
                        addNewRuleBasedInboxThread(data?.data);
                    });
                }

                // Handle Assign, Join, Delete Agent.
                // Handle manual agent actions for ruleBasedInbox
                // Check raw thread_execution_data for manual agent actions (not filtered)
                const rawThreadExecutionData = data?.data?.thread_execution_data || [];
                handleRuleBasedInboxManualAgents(data?.data, rawThreadExecutionData, isChatOpen);
            }

            break;

        case "static_workflow_inbox_activity":
            // Handle thread metadata updates (status, tags, notes, archive)
            if (isRuleBasedInbox) {
                handleRuleBasedInboxActivity(data?.data || data);
            }
            break;

        default:
            break;
    }
}

function createSocketConnection(accessToken, serverUrl) {
    document.cookie = `cr-ssid=${accessToken}; path=/; SameSite=Lax`;

    return io(serverUrl, {
        path: '/notification/socket.io',
        auth: { token: accessToken },
        query: { token: accessToken },
        transports: ['polling'],
        withCredentials: true,
        timeout: 20000,
        forceNew: true,
        reconnection: false,
        reconnectionAttempts: 0,
        reconnectionDelay: 0,
        reconnectionDelayMax: 0
    });
}

function stopPingInterval() {
    const { pingInterval, setPingInterval } = useSocketStore.getState();
    if (pingInterval) {
        clearInterval(pingInterval);
        setPingInterval(null);
    }
}

function startPingMechanism(socket) {
    const { setPingInterval } = useSocketStore.getState();
    stopPingInterval();
    const interval = setInterval(() => {
        if (socket && socket.connected) {
            const pingData = { timestamp: Date.now(), socketId: socket.id };
            socket.emit('ping', pingData);
        } else {
            stopPingInterval();
        }
    }, 30000);
    setPingInterval(interval);
}

function scheduleReconnectionAttempt() {
    const {
        setReconnectTimer,
        socketReconnectDelays,
        socketReconnectAttempts,
        setSocketReconnectAttempts,
        flagExplicitSocketDisconnect,
        setFlagShowSocketConnectionLossPopup,
        maxReconnectAttemptsReached,
        setMaxReconnectAttemptsReached
    } = useSocketStore.getState();

    const { showWarning } = useGenericStore.getState();

    // Check if max attempts already reached - prevent infinite loops
    if (maxReconnectAttemptsReached) {
        console.log("Max reconnection attempts reached. Stopping reconnection attempts.");
        return;
    }

    // Check if explicit disconnect or exceeded delay array length
    if (flagExplicitSocketDisconnect) {
        return;
    }

    // Check if we've exceeded the maximum reconnection attempts
    if (socketReconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        console.log(`Max reconnection attempts (${MAX_RECONNECT_ATTEMPTS}) reached. Stopping reconnection.`);
        setMaxReconnectAttemptsReached(true);
        setFlagShowSocketConnectionLossPopup(true);
        showWarning('Failed to reconnect after multiple attempts. Please refresh the page.');
        return;
    }

    // Use delay from array if available, otherwise use exponential backoff
    const delay = socketReconnectAttempts < socketReconnectDelays.length
        ? socketReconnectDelays[socketReconnectAttempts]
        : Math.min(60000 * Math.pow(2, socketReconnectAttempts - socketReconnectDelays.length), 300000); // Max 5 minutes

    const updated_reconnect_attempts = socketReconnectAttempts + 1;
    setSocketReconnectAttempts(updated_reconnect_attempts);

    console.log(`Reconnection attempt ${updated_reconnect_attempts}/${MAX_RECONNECT_ATTEMPTS} scheduled in ${delay}ms`);
    if (socketReconnectAttempts > 1) { // Only show warning, after 2 attempt.
        showWarning('Trying to reconnect...');
    }

    if (delay === 0) {
        performReconnection();
    } else {
        setReconnectTimer(setTimeout(performReconnection, delay));
    }
}

async function performReconnection() {
    const {
        socketConnectionAccessToken,
        flagExplicitSocketDisconnect,
        socketObj,
        setSocketObj,
        setFlagShowSocketConnectionLossPopup,
        setSocketConnectionAccessToken,
        maxReconnectAttemptsReached,
        setMaxReconnectAttemptsReached,
        socketServerUrl
    } = useSocketStore.getState();

    // Prevent reconnection if max attempts reached or explicit disconnect
    if (flagExplicitSocketDisconnect || maxReconnectAttemptsReached) {
        return;
    }

    const serverUrl = socketServerUrl;
    if (!serverUrl || !socketConnectionAccessToken) {
        console.log("serverUrl and socketConnectionAccessToken is: ", socketConnectionAccessToken);
        setFlagShowSocketConnectionLossPopup(true);
        setMaxReconnectAttemptsReached(true);
        return;
    }

    // Clean up old socket
    if (socketObj) {
        socketObj.removeAllListeners();
        socketObj.disconnect();
        setSocketObj(null);
    }

    try {
        // UserMe Call Before Reconnection
        const userDetailResponse = await agent.UserMeAPI.get();

        if (userDetailResponse?.status && userDetailResponse?.data?.[0]?.access_token) {
            setSocketConnectionAccessToken(userDetailResponse?.data?.[0]?.access_token);

            const socket = createSocketConnection(userDetailResponse?.data?.[0]?.access_token, serverUrl);
            setSocketObj(socket);
            setUpSocketHandlers(socket);
        } else {
            // UserMe responded without a valid token — keep the reconnection cycle going.
            console.error("Failed to get access token for reconnection");
            scheduleReconnectionAttempt();
        }
    } catch (error) {
        // UserMe failed (e.g. no network). Schedule the next attempt so the
        // increment cycle continues instead of stalling on the first try.
        console.error("Error during reconnection:", error);
        scheduleReconnectionAttempt();
    }
}

/**
 * Immediately attempt to re-establish the socket connection when the browser
 * regains network connectivity (window "online" event).
 *
 * This short-circuits any pending backoff timer so the user does not have to
 * wait out the remaining delay, and resets the attempt counter so a fresh
 * reconnection cycle can run.
 */
export const reconnectSocketOnNetworkRestored = () => {
    const {
        socketObj,
        flagExplicitSocketDisconnect,
        socketConnectionAccessToken,
        socketServerUrl,
        reconnectTimer,
        setReconnectTimer,
        setSocketReconnectAttempts,
        setMaxReconnectAttemptsReached,
    } = useSocketStore.getState();

    // Do nothing if the user explicitly disconnected (e.g. logged out).
    // Nothing to reconnect with — we were never connected with a session.
    // Already have a live connection — no need to reconnect.
    if (flagExplicitSocketDisconnect || (!socketConnectionAccessToken || !socketServerUrl) || (socketObj && socketObj.connected)) {
        return;
    }

    // Cancel any pending backoff timer so we reconnect right away.
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        setReconnectTimer(null);
    }

    // Network is back: reset the cycle so it can run cleanly from the start.
    setSocketReconnectAttempts(0);
    setMaxReconnectAttemptsReached(false);

    performReconnection();
};

function setUpSocketHandlers(socket) {
    if (!socket) return;
    const {
        setSocketObj,
        reconnectTimer,
        setReconnectTimer,
        setSocketReconnectAttempts,
        flagExplicitSocketDisconnect,
        setFlagExplicitSocketDisconnect,
        socketReconnectAttempts,
        setMaxReconnectAttemptsReached,
        setFlagShowSocketConnectionLossPopup,
        setLastConnectionTimestamp,
        lastConnectionTimestamp
    } = useSocketStore.getState();

    socket.on("connect", () => {
        console.log("Socket Connected..");

        // Track connection timestamp to detect immediate disconnections
        const connectionTime = Date.now();
        setLastConnectionTimestamp(connectionTime);

        // Reset reconnection state on successful connection
        setSocketReconnectAttempts(0);
        setMaxReconnectAttemptsReached(false);
        // Dismiss the connection-loss popup if it was shown after giving up.
        setFlagShowSocketConnectionLossPopup(false);
        if (reconnectTimer) {
            clearTimeout(reconnectTimer);
            setReconnectTimer(null);
        }

        startPingMechanism(socket);
    });


    socket.on("disconnect", (reason) => {
        console.log("Socket Dis-Connected..", reason);
        stopPingInterval();
        setSocketObj(null);

        const { lastConnectionTimestamp: currentLastConnectionTimestamp, maxReconnectAttemptsReached: currentMaxReconnectAttemptsReached } = useSocketStore.getState();

        // Check if this was an immediate disconnect (connection lasted less than MIN_STABLE_CONNECTION_TIME)
        const isImmediateDisconnect = currentLastConnectionTimestamp &&
            (Date.now() - currentLastConnectionTimestamp) < MIN_STABLE_CONNECTION_TIME;

        if (isImmediateDisconnect) {
            console.warn(`Immediate disconnect detected (connection lasted ${Date.now() - currentLastConnectionTimestamp}ms). This may indicate a server-side issue.`);
        }

        if (!flagExplicitSocketDisconnect && reason.toLowerCase() != "io client disconnect") {
            // Only attempt reconnection if max attempts not reached
            if (!currentMaxReconnectAttemptsReached) {
                scheduleReconnectionAttempt();
            } else {
                console.log("Max reconnection attempts reached. Not attempting reconnection.");
            }
        } else {
            setFlagExplicitSocketDisconnect(false);
            setSocketReconnectAttempts(0);
            setMaxReconnectAttemptsReached(false);
            setLastConnectionTimestamp(null);
        }
    });

    socket.on("connect_error", (error) => {
        console.log("Error while Connecting Socket..", error);
        stopPingInterval();
        setSocketObj(null);

        const { maxReconnectAttemptsReached: currentMaxReconnectAttemptsReached } = useSocketStore.getState();

        if (!flagExplicitSocketDisconnect && error.message?.toLowerCase() != "io client disconnect") {
            // Only attempt reconnection if max attempts not reached
            if (!currentMaxReconnectAttemptsReached) {
                scheduleReconnectionAttempt();
            } else {
                console.log("Max reconnection attempts reached. Not attempting reconnection.");
            }
        } else {
            setFlagExplicitSocketDisconnect(false);
            setSocketReconnectAttempts(0);
            setMaxReconnectAttemptsReached(false);
            setLastConnectionTimestamp(null);
        }
    });

    socket.on("message", (data) => {
        AddMessage(data);
    });

    socket.on("error", (error) => {
        console.log("Error in Socket..", error);
        setSocketObj(null);
    });
}

export const connectSocket = (accessToken, vendorData) => {
    if (!accessToken) {
        console.error("Access Token is Missing, to connect socket");
        return;
    }

    const {
        socketObj,
        setSocketObj,
        setSocketConnectionAccessToken,
        setSocketReconnectAttempts,
        setMaxReconnectAttemptsReached,
        setLastConnectionTimestamp,
        setSocketServerUrl
    } = useSocketStore.getState();

    // Reset reconnection state when manually connecting
    setSocketReconnectAttempts(0);
    setMaxReconnectAttemptsReached(false);
    setLastConnectionTimestamp(null);

    setSocketConnectionAccessToken(accessToken);
    // const webSocketUrl = `${process.env.REACT_APP_BASE_URL}`;
    const webSocketUrl = getVendorField(vendorData ? vendorData : null, "backend api", process.env.REACT_APP_BASE_URL);
    setSocketServerUrl(webSocketUrl);

    if (!socketObj) {
        const socket = createSocketConnection(accessToken, webSocketUrl);
        setSocketObj(socket);

        setUpSocketHandlers(socket);
    }
}

export const disConnectSocket = () => {
    const {
        socketObj,
        setSocketObj,
        setFlagExplicitSocketDisconnect,
        setSocketReconnectAttempts,
        setMaxReconnectAttemptsReached,
        setLastConnectionTimestamp,
        reconnectTimer,
        setReconnectTimer
    } = useSocketStore.getState();

    setFlagExplicitSocketDisconnect(true);
    stopPingInterval();

    // Clear any pending reconnection timers
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        setReconnectTimer(null);
    }

    // Reset reconnection state
    setSocketReconnectAttempts(0);
    setMaxReconnectAttemptsReached(false);
    setLastConnectionTimestamp(null);

    if (socketObj) {
        socketObj.disconnect();
        setSocketObj(null);
    }
}